'use client';

import React, { useRef } from 'react';
import { asObject, type FieldValue } from '../formFieldTypes';

/**
 * PassFailField — Three-state toggle (Pass / Fail / N/A) with conditional evidence
 *
 * Used in all compliance and inspection forms:
 * - Electrical CoC: circuit test results
 * - Vehicle Pre-Trip: component checks
 * - Safety Audit: compliance checkpoints
 *
 * validation.fail_action       — 'block' | 'warn' | 'flag'
 * options.allow_na             — Show N/A option
 * options.require_comment_on_fail  — Force notes when failing
 * options.require_photo_on_fail    — Force photo evidence on failure
 */

type PassFailOptions = {
      allow_na?: boolean;
      require_comment_on_fail?: boolean;
      require_photo_on_fail?: boolean;
      [key: string]: unknown;
};

type PassFailValidation = {
      fail_action?: 'block' | 'warn' | 'flag';
      [key: string]: unknown;
};

interface PassFailFieldProps {
  field: {
    key: string;
    label: string;
    required?: boolean;
    readonly?: boolean;
    validation?: unknown;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue; // { result: 'pass'|'fail'|'na', comment?: string }
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

export const PassFailField: React.FC<PassFailFieldProps> = ({ field, value, onChange, isDark }) => {
  const opts = (field.options ?? {}) as PassFailOptions;
  const validation = (field.validation ?? {}) as PassFailValidation;
  const allowNA = opts?.allow_na !== false;
  const requireCommentOnFail = opts?.require_comment_on_fail ?? false;
  const requirePhotoOnFail = opts?.require_photo_on_fail ?? false;
  const failAction = validation?.fail_action || 'warn';
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Normalize value
  const valueObj = asObject(value);
  const result: string | null = valueObj ? (valueObj.result as string | null) : (typeof value === 'string' ? value : null);
  const comment: string = valueObj ? ((valueObj.comment as string | undefined) || '') : '';
  const photo: string | undefined = valueObj ? (valueObj.photo as string | undefined) : undefined;

  const handleResultChange = (newResult: string) => {
    if (field.readonly) return;
    if (result === newResult) {
      // Toggle off
      onChange(null);
    } else {
      onChange({ result: newResult, comment: newResult === 'fail' ? comment : '', ...(newResult === 'fail' && photo ? { photo } : {}) });
    }
  };

  const handleCommentChange = (newComment: string) => {
    onChange({ result, comment: newComment, ...(photo ? { photo } : {}) });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      onChange({ result, comment, photo: String(reader.result) });
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    onChange({ result, comment });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isFail = result === 'fail';
  const showCommentBox = isFail && requireCommentOnFail;
  const showPhotoBox = isFail && requirePhotoOnFail;

  const buttonBase = `flex-1 flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all border-2 min-w-0`;

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5 sm:gap-2">
        {/* Pass button */}
        <button
          type="button"
          onClick={() => handleResultChange('pass')}
          disabled={field.readonly}
          className={`${buttonBase} ${
            result === 'pass'
              ? 'bg-emerald-500 border-emerald-500 text-white shadow-md'
              : isDark
                ? 'bg-gray-800 border-gray-600 text-gray-400 hover:border-emerald-500 hover:text-emerald-400'
                : 'bg-white border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-600'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Pass
        </button>

        {/* Fail button */}
        <button
          type="button"
          onClick={() => handleResultChange('fail')}
          disabled={field.readonly}
          className={`${buttonBase} ${
            result === 'fail'
              ? failAction === 'block'
                ? 'bg-red-500 border-red-500 text-white shadow-md'
                : 'bg-amber-500 border-amber-500 text-white shadow-md'
              : isDark
                ? 'bg-gray-800 border-gray-600 text-gray-400 hover:border-red-500 hover:text-red-400'
                : 'bg-white border-gray-300 text-gray-500 hover:border-red-500 hover:text-red-600'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Fail
        </button>

        {/* N/A button */}
        {allowNA && (
          <button
            type="button"
            onClick={() => handleResultChange('na')}
            disabled={field.readonly}
            className={`${buttonBase} ${
              result === 'na'
                ? isDark
                  ? 'bg-gray-600 border-gray-500 text-gray-200 shadow-md'
                  : 'bg-gray-400 border-gray-400 text-white shadow-md'
                : isDark
                  ? 'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-400 hover:text-gray-300'
                  : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600'
            }`}
          >
            N/A
          </button>
        )}
      </div>

      {/* Fail severity indicator */}
      {isFail && (
        <div className={`flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-md ${
          failAction === 'block'
            ? 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
            : failAction === 'flag'
              ? 'bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800'
              : 'bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
        }`}>
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
          </svg>
          {failAction === 'block' ? 'This failure blocks form submission' : failAction === 'flag' ? 'Flagged for review' : 'Warning — review before submitting'}
        </div>
      )}

      {/* Comment box on failure */}
      {(showCommentBox || (isFail && comment)) && (
        <textarea
          value={comment}
          onChange={(e) => handleCommentChange(e.target.value)}
          placeholder="Describe the failure or deficiency..."
          rows={2}
          className={`block w-full rounded-md px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-teal-500 ${
            isDark
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
          required={requireCommentOnFail && isFail}
        />
      )}

      {/* Photo evidence on failure */}
      {(showPhotoBox || (isFail && photo)) && (
        <div className="space-y-1.5">
          {photo ? (
            <div className="relative">
              <img src={photo} alt="Failure evidence" className="rounded-md max-h-40 w-auto border border-gray-300 dark:border-gray-600" />
              {!field.readonly && (
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-1 hover:bg-black/80"
                  title="Remove photo"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          ) : (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                disabled={field.readonly}
                className={`block w-full text-xs rounded-md border file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-white file:bg-teal-500 ${
                  isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                }`}
                required={requirePhotoOnFail && isFail}
              />
              <p className={`text-[11px] mt-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                Photo evidence is required for this failure.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PassFailField;
